import {
	Alert,
	Button,
	DatePicker,
	Form,
	Select,
	TimePicker
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
	addAperturaDeCaja,
	updateAperturaDeCaja,
} from "../../services/aperturaDeCajaService";
import { getAllCajas } from "../../services/cajaService";
import { getAllTurnos } from "../../services/turnoService";
import { getAllUsers } from "../../services/userService";
import DenominacionesCalculator from "../common/DenominacionesCalculator";

const AperturaDeCajaForm = ({
	onAperturaAdded,
	onAperturaUpdated,
	aperturaToEdit,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [users, setUsers] = useState([]);
	const [turnos, setTurnos] = useState([]);
	const [cajas, setCajas] = useState([]);
	const { currentUser } = useAuth();
	const [importeApertura, setImporteApertura] = useState(0);

	// Cargar cajeros (usuarios), turnos y cajas
	useEffect(() => {
		const loadData = async () => {
			try {
				const usersData = await getAllUsers();
				setUsers(usersData);

				const turnosData = await getAllTurnos();
				setTurnos(turnosData);

				const cajasData = await getAllCajas();
				setCajas(cajasData);

				// Establecer el primer turno por defecto si no estamos editando
				if (!aperturaToEdit && turnosData.length > 0) {
					form.setFieldValue('Turno', turnosData[0].No || turnosData[0].id);
				}
			} catch (error) {
				console.error("Error al cargar datos:", error);
				setError("Error al cargar datos de cajeros, turnos y cajas");
			}
		};

		loadData();
	}, [form, aperturaToEdit]);

	// Cargar datos de la apertura si estamos en modo edición
	useEffect(() => {
		if (aperturaToEdit) {
			setImporteApertura(aperturaToEdit.ImporteDeApertura || 0);
			form.setFieldsValue({
				Cajero: aperturaToEdit.Cajero,
				FechaDeApertura: aperturaToEdit.FechaDeApertura
					? dayjs(aperturaToEdit.FechaDeApertura)
					: null,
				HoraDeApertura: aperturaToEdit.HoraDeApertura
					? dayjs(aperturaToEdit.HoraDeApertura, "HH:mm")
					: null,
				Estado: aperturaToEdit.Estado,
				Turno: aperturaToEdit.Turno,
				Caja: aperturaToEdit.Caja,
			});
			setIsEditing(true);
		} else {
			setImporteApertura(0);
			form.resetFields();
			// Establecer valores predeterminados
			const horaActual = dayjs();
			form.setFieldsValue({
				FechaDeApertura: dayjs(),
				HoraDeApertura: horaActual,
				Estado: "Abierto", // Estado por defecto
				Cajero: currentUser?.No, // Establecer el usuario actual como cajero predeterminado
			});
			setIsEditing(false);
		}
	}, [aperturaToEdit, form, currentUser]);

	const onFinish = async (values) => {
		console.log(values);
		try {
			// Limpiar error previo
			setError("");
			setLoading(true);

			// Incluir el importe desde nuestro state
			const formattedValues = {
				...values,
				ImporteDeApertura: importeApertura,
				FechaDeApertura: values.FechaDeApertura.format("YYYY-MM-DD"),
				HoraDeApertura: values.HoraDeApertura.format("HH:mm"),
			};

			if (isEditing) {
				// Actualizar apertura existente
				const updatedApertura = await updateAperturaDeCaja(aperturaToEdit.id, {
					...formattedValues,
					pendingSync: 1,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Apertura de caja actualizada correctamente");

				// Notificar al componente padre
				if (onAperturaUpdated) onAperturaUpdated(updatedApertura);
			} else {
				// Guardar apertura en IndexedDB
				const result = await addAperturaDeCaja(formattedValues);

				// Mostrar mensaje de éxito
				window.messageApi.success("Apertura de caja registrada correctamente");

				// Limpiar formulario
				form.resetFields();
				setImporteApertura(0);
				// Establecer valores predeterminados
				const horaActual = dayjs();
				form.setFieldsValue({
					FechaDeApertura: dayjs(),
					HoraDeApertura: horaActual,
					Estado: "Abierto",
					Cajero: currentUser?.No, // Restablecer el usuario actual como cajero predeterminado
				});

				// Notificar al componente padre
				if (onAperturaAdded) onAperturaAdded(result);
			}
		} catch (error) {
			// Mostrar error específico
			setError(error.message || "Error al procesar la apertura de caja");
			console.error("Error al procesar apertura de caja:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setImporteApertura(0);
		// Establecer valores predeterminados
		const horaActual = dayjs();
		form.setFieldsValue({
			FechaDeApertura: dayjs(),
			HoraDeApertura: horaActual,
			Estado: "Abierto",
			Cajero: currentUser?.No, // Restablecer el usuario actual como cajero predeterminado
		});
		setIsEditing(false);
		if (onAperturaUpdated) onAperturaUpdated(null);
	};

	const estadoOptions = [
		{ value: "Abierto", label: "Abierto" },
		{ value: "Cerrado", label: "Cerrado" },
		{ value: "Turno Generado", label: "Turno Generado" },
	];

	const handleImporteChange = (nuevoImporte) => {
		setImporteApertura(nuevoImporte);
	};

	return (
		<div className="apertura-caja-form-container">
			<h2>
				{isEditing ? "Editar Apertura de Caja" : "Registro de Apertura de Caja"}
			</h2>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Form
				form={form}
				name="apertura-caja-form"
				onFinish={onFinish}
				layout="vertical"
				scrollToFirstError
			>
				<Form.Item
					name="Cajero"
					label="Cajero"
					rules={[
						{
							required: true,
							message: "Por favor seleccione el cajero",
						},
					]}
				>
					<Select
						placeholder="Seleccione un cajero"
						loading={users.length === 0}
						showSearch
						optionFilterProp="children"
						disabled
						filterOption={(input, option) =>
							(option?.label || "").toLowerCase().includes(input.toLowerCase())
						}
					>
						{users.map((user) => (
							<Select.Option key={user.id} value={user.No}>
								{user.firstName} {user.lastName}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="Caja"
					label="Caja"
					rules={[
						{
							required: true,
							message: "Por favor seleccione la caja",
						},
					]}
				>
					<Select
						placeholder="Seleccione una caja"
						loading={cajas.length === 0}
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							(option?.label || "").toLowerCase().includes(input.toLowerCase())
						}
					>
						{cajas.map((caja) => (
							<Select.Option key={caja.id} value={caja.No}>
								{caja.nombre}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="FechaDeApertura"
					label="Fecha de Apertura"
					rules={[
						{
							required: true,
							message: "Por favor seleccione la fecha de apertura",
						},
					]}
				>
					<DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
				</Form.Item>

				<Form.Item
					name="HoraDeApertura"
					label="Hora de Apertura"
					rules={[
						{
							required: true,
							message: "Por favor seleccione la hora de apertura",
						},
					]}
				>
					<TimePicker disabled style={{ width: "100%" }} format="HH:mm" minuteStep={5} />
				</Form.Item>

				<Form.Item
					label="Importe de Apertura"
					required
					tooltip="Utilice el botón de calculadora para ingresar el importe por denominaciones"
				>
					<DenominacionesCalculator
						value={importeApertura}
						onChange={handleImporteChange}
						modalTitle="Cálculo de Importe por Denominaciones"
						placeholder="Importe de apertura"
					/>
				</Form.Item>

				<Form.Item
					name="Estado"
					label="Estado"
					rules={[
						{
							required: true,
							message: "Por favor seleccione el estado",
						},
					]}
				>
					<Select placeholder="Seleccione el estado" disabled>
						{estadoOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="Turno"
					label="Turno"
					style={{ display: "none" }}
					rules={[
						{
							required: true,
							message: "Por favor seleccione el turno",
						},
					]}
				>
					<Select
						placeholder="Seleccione un turno"
						loading={turnos.length === 0}
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							(option?.label || "").toLowerCase().includes(input.toLowerCase())
						}
					>
						{turnos.map((turno) => (
							<Select.Option key={turno.No || turno.id} value={turno.No || turno.id}>
								{turno.DescripcionTurno}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item>
					<div style={{ display: "flex", gap: "10px" }}>
						<Button type="primary" htmlType="submit" loading={loading} block>
							{isEditing
								? "Actualizar Apertura de Caja"
								: "Registrar Apertura de Caja"}
						</Button>

						{isEditing && (
							<Button onClick={handleCancel} block>
								Cancelar
							</Button>
						)}
					</div>
				</Form.Item>
			</Form>


		</div>
	);
};

export default AperturaDeCajaForm;
